import React, { useEffect, useState } from "react";
import { ResponsiveDeviceType } from "../../layouts/ScreenMedia";
import { useSelector } from "react-redux";
import { RootState } from "../../../libraries/state_reducers/store";
import Div from "../../common/ui/Div";
import { TextBlock } from "../../common/ui/Text";
import UtilityHelper from "../../../service/utilities/UtilityHelper";
import AppTranslation from "../../../service/app_translation/AppTranslation";
import Center from "../../common/ui/Center";
import { LogoRound } from "../../common/ui/Logo";
import SessionStorage from "../../../service/storage/SessionStorage";
import StorageKeys from "../../../const/StorageKeys";
import { textCase } from "../../../service/ui/UI";
import { ButtonSingle } from "../../common/form/Button";
import Loader from "../../common/ui/Loader";
import SocketService, { socket } from "../../../service/socket/SocketService";
import { useDispatch } from "react-redux";
import { removeAllModal } from "../../../libraries/state_reducers/widget/ModalsReducer";
import SocketChatsKeys from "../../../const/SocketChatsKeys";
import { AlertConfirm } from "../../common/ui/Alerts";
import { ConversationList } from "../../../model/chats/ChatListModel";
import { navigate } from "../../../service/navigation/Navigation";
import { appRoutes } from "../../../routes/routes";
import PincodeInput from "../../common/ui/PincodeInput";
import CacheStorage from "../../../service/storage/CacheStorage";
import AppConfigService from "../../../service/app_config/AppConfigService";

const DetailConnectGroup = () => {
  const type: number = ResponsiveDeviceType();

  const dispatch = useDispatch();
  const pincodeLength: number = 6;

  const detailGroup = useSelector(
    (state: RootState) => state.detailConnectGroup.detailGroup
  );

  const branchLogo = CacheStorage.getValue(StorageKeys.branch_logo) || "";
  const branchName = CacheStorage.getValue(StorageKeys.branch_name) || "";

  const [showInput, setShowInput] = useState<boolean>(false);
  const [code, setCode] = useState<string>("");

  const connectChat = async (profId: string = "") => {
    if (detailGroup != null) {
      Loader.modal();
      const connectStatus = await SocketService.initialize();
      await UtilityHelper.delay();
      Loader.modal(false);

      if (connectStatus) {
        Loader.modal();
        await SocketService.initConversation({
          flag: "3",
          eventN: UtilityHelper.isNotEmpty(profId)
            ? SocketChatsKeys.request_user_group_chat
            : SocketChatsKeys.join_chat_group,
          convId: detailGroup.group_conv_id ?? "",
          profId: profId,
          callback: async (response: any) => {
            console.log(response);
            if (response["status"] === "1") {
              navigateTo(response["results"]);
            }
          },
        });
      }
    }
  };

  const onClickConnect = async () => {
    if (detailGroup != null) {
      if (UtilityHelper.isNotEmpty(detailGroup?.passcode ?? "")) {
        setShowInput(true);
      } else {
        connectProper();
      }
    }
  };

  const connectProper = () => {
    if (detailGroup != null) {
      if (detailGroup.group_join_status === "1") {
        connectChat();
      } else {
        AlertConfirm({
          title: "are_you_sure_you_wish_to_join_the_group",
          onConfirm: () => {
            connectChat(detailGroup?.group_id);
          },
        });
      }
    }
  };

  const navigateTo = async (detail: any) => {
    dispatch(removeAllModal());
    Loader.modal(false);

    const conversDetail: ConversationList = {
      conv_id: detail["conv_id"],
      flag: detail["flag"],
      account_id: "0",
      account_type: "1",
      name: detail["group_name"],
      title: "group",
      logo: detail["group_logo"],
      date: "created_by" in detail ? detail["created_by"] : "",
      unread_count: detail["conv_settings"]["unread_count"],
      conv_settings: {
        unread_count: detail["conv_settings"]["unread_count"],
        mute_status:
          detail["conv_settings"]["muted_conversations"]["mute_status"],
      },
    };

    SessionStorage.saveValue(
      StorageKeys.chats_chat_selected,
      JSON.stringify(conversDetail)
    );

    SessionStorage.saveValue(StorageKeys.chats_popup_connect, "1");

    navigate(appRoutes.routeNameChats);
    if (type < 3) {
      navigate(appRoutes.routeNameChatGroup);
    }
  };

  const verifyCode = (codeVal: string) => {
    if (codeVal.length === pincodeLength && code === detailGroup?.passcode) {
      connectProper();
    }
  };

  useEffect(() => {
    verifyCode(code);
    // eslint-disable-next-line
  }, [code]);

  useEffect(() => {
    return () => {
      if (socket.connected) {
        SocketService.disconnect();
        console.log(`socket disconnect - DEPT popup connect`);
      }
    };
  }, []);

  return (
    <Div className={`pt-4 pb-${type === 3 ? 1 : 3}`}>
      {UtilityHelper.isNotEmpty(detailGroup?.passcode ?? "") && (
        <TextBlock
          center={true}
          fontSize="20_21"
          fontName="bold"
          textColor="validation"
        >
          {AppTranslation.text("restricted_access")}
        </TextBlock>
      )}

      <Center className="py-3">
        <LogoRound
          url={branchLogo || AppConfigService.get("company_logo")}
          isProfile={true}
          name={branchName}
          size={type === 2 ? 180 : 90}
        />
      </Center>
      <TextBlock
        center={true}
        fontSize="20_21"
        fontName="bold"
        textColor="theme-label"
      >
        {textCase(detailGroup?.group_title ?? "")}
      </TextBlock>

      {UtilityHelper.isNotEmpty(detailGroup?.default_message ?? "") && (
        <TextBlock
          center={true}
          fontSize="16_17"
          fontName="book"
          textColor="theme-label"
          className="pt-3"
        >
          {textCase(detailGroup?.default_message ?? "")}
        </TextBlock>
      )}

      {UtilityHelper.isNotEmpty(detailGroup?.passcode ?? "") && (
        <TextBlock
          center={true}
          fontSize="13_14"
          fontName="book"
          textColor="primary"
          className="pt-3"
        >
          {AppTranslation.text("join_with_passcode")}
        </TextBlock>
      )}

      <Div className="pt-4">
        {showInput ? (
          <PincodeInput
            length={pincodeLength}
            onChange={setCode}
            resetStatus={false}
            layout="clean"
          />
        ) : (
          <ButtonSingle handleClick={onClickConnect}>
            {AppTranslation.text("connect")}
          </ButtonSingle>
        )}

        {UtilityHelper.isNotEmpty(detailGroup?.passcode ?? "") &&
          code.length === pincodeLength &&
          code !== detailGroup?.passcode &&
          showInput && (
            <TextBlock
              center={true}
              fontSize="13_14"
              fontName="book"
              textColor="primary"
              className="pt-3"
            >
              {AppTranslation.text(
                "invalid_passcode_please_enter_valid_passcode"
              )}
            </TextBlock>
          )}
      </Div>
    </Div>
  );
};

export default DetailConnectGroup;
